import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';

import type { StateWithSidekick, StateWithSidekickChat } from '../state';
import { readLifecycleSnapshot } from './snapshot';
import { createLifecycleTracker } from './tracker';
import type { SidekickLifecycleCallback } from './types';

/**
 * Reports Sidekick lifecycle events to the host while mounted. Reads
 * every store update rather than each render, so actions React batches
 * together, like creating a conversation and adding its first question,
 * are still told apart. Never re-renders the caller.
 *
 * Unmounting ends the open session, and so does `pagehide`, since a dock
 * that outlives navigation is never unmounted by a tab close or refresh.
 * A page restored from the back-forward cache picks the session back up.
 * Hiding the tab alone ends nothing, so a tab switch does not split a
 * session in two. A callback that throws is ignored, so tracking can
 * never break the chat.
 * @param onLifecycleEvent - Host callback; omit it to track nothing.
 */
export const useSidekickLifecycleEvents = (
  onLifecycleEvent?: SidekickLifecycleCallback
): void => {
  const store = useStore<StateWithSidekick & StateWithSidekickChat>();
  const onLifecycleEventRef = useRef(onLifecycleEvent);
  const isTracking = onLifecycleEvent !== undefined;

  // Adopted after commit, not during render: a switch of acting
  // organization resets the chat in a layout effect, and the session
  // that reset ends belongs to the callback it ran under. A callback
  // taken away keeps the last one, so the cleanup below can still end
  // the open session through it.
  useEffect(() => {
    if (onLifecycleEvent !== undefined) {
      onLifecycleEventRef.current = onLifecycleEvent;
    }
  });

  useEffect(() => {
    if (!isTracking) return undefined;
    const readSnapshot = () => readLifecycleSnapshot(store.getState());
    const tracker = createLifecycleTracker(readSnapshot(), (event) => {
      try {
        onLifecycleEventRef.current?.(event);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn(
            '[useSidekickLifecycleEvents] host callback threw',
            error
          );
        }
      }
    });
    const unsubscribe = store.subscribe(() => {
      tracker.sync(readSnapshot());
    });
    const handlePageHide = () => {
      tracker.pause();
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        tracker.resume(readSnapshot());
      }
    };
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      unsubscribe();
      tracker.pause();
    };
  }, [isTracking, store]);
};
